import { useEffect, useRef, useState } from "react";
import { ArrowRight, Building2, KeyRound, LogOut, QrCode, ShieldCheck } from "lucide-react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { needsPasswordSetup, postLoginDestination } from "../auth/access";
import { Logo } from "../components/Logo";
import { ExternalRedirect } from "../components/ExternalRedirect";
import { runtimeUrls, tenantUrl } from "../config/domains";
import { AdminConsole } from "./AdminConsole";

export function AccessErrorPage({ code, title, message }: { code: string; title: string; message: string }) {
  return <main className="access-error"><Logo/><span>{code}</span><h1>{title}</h1><p>{message}</p><a className="button primary" href={runtimeUrls().appUrl}>Ir a acceso central<ArrowRight size={17}/></a></main>;
}

export function AppHomePage() {
  const { user, loading, accessContext } = useAuth();
  if (loading) return <div className="app-loader"><span/><strong>POSITRONICO</strong></div>;
  if (!user) return <Navigate to="/login" replace/>;
  return <ExternalRedirect to={postLoginDestination(accessContext)}/>;
}

export function WorkspacesPage() {
  const { user, loading, accessContext, signOut } = useAuth();
  if (loading) return <div className="app-loader"><span/><strong>POSITRONICO</strong></div>;
  if (!user) return <Navigate to="/login" replace/>;
  const active = accessContext.workspaces.filter((workspace) => workspace.status === "ACTIVE" && workspace.organizationActive);
  return <main className="access-hub"><header><Logo/><button className="button secondary" onClick={() => void signOut()}><LogOut size={16}/>Cerrar sesión</button></header><section><span className="eyebrow">TUS ORGANIZACIONES</span><h1>Elige un workspace</h1><p>Tu sesión es válida en todos los portales POSITRONICO a los que tienes acceso.</p>{active.length ? <div className="workspace-grid">{active.map((workspace) => <a key={workspace.organizationId} href={tenantUrl(workspace.slug)}><span><Building2/></span><div><strong>{workspace.name}</strong><small>{workspace.slug}.positronico.com · {workspace.role === "CLIENT_ADMIN" ? "Administrador" : workspace.role === "CLIENT_MEMBER" ? "Miembro" : "Owner"}</small></div><ArrowRight/></a>)}</div> : <div className="no-access"><ShieldCheck/><h2>No tienes accesos activos</h2><p>Solicita una invitación al administrador de tu organización.</p></div>}</section></main>;
}

export function AdminPage() {
  return <AdminConsole/>;
}

export function MfaPage() {
  const { user, loading, accessContext, getMfaStatus, enrollMfa, challengeMfa, verifyMfa, signOut } = useAuth();
  const navigate = useNavigate();
  const [factorId, setFactorId] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [setupAttempt, setSetupAttempt] = useState(0);
  useEffect(() => {
    if (!user || !accessContext.isPositronicoOwner || accessContext.ownerAuthorized) return;
    let active = true;
    setBusy(true);
    setMessage(null);
    setFactorId("");
    setChallengeId("");
    setQrCode("");
    void getMfaStatus().then(async (status) => {
      if (!active) return;
      if (status.aal === "aal2") { navigate("/admin", { replace: true }); return; }
      const verified = status.factors.find((factor) => factor.status === "verified");
      if (verified) {
        const challenge = await challengeMfa(verified.id);
        if (active) { setFactorId(verified.id); setChallengeId(challenge); }
      } else {
        const enrollment = await enrollMfa();
        const challenge = await challengeMfa(enrollment.factor_id);
        if (active) { setFactorId(enrollment.factor_id); setChallengeId(challenge); setQrCode(enrollment.qr_code); }
      }
    }).catch(() => { if (active) setMessage("No pudimos preparar la verificación en dos pasos. Inténtalo de nuevo."); }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [accessContext.isPositronicoOwner, accessContext.ownerAuthorized, challengeMfa, enrollMfa, getMfaStatus, navigate, setupAttempt, user]);
  if (loading || busy) return <div className="app-loader"><span/><strong>Preparando MFA</strong></div>;
  if (!user) return <Navigate to="/login" replace/>;
  if (!accessContext.isPositronicoOwner) return <AccessErrorPage code="403" title="MFA no requerido" message="Esta verificación está reservada para el Owner."/>;
  if (accessContext.ownerAuthorized) return <Navigate to="/admin" replace/>;
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setMessage(null);
    const code = String(new FormData(event.currentTarget).get("code") ?? "");
    const result = await verifyMfa(factorId, challengeId, code); setBusy(false);
    if (result.error) setMessage(result.error); else navigate("/admin", { replace: true });
  };
  return <main className="auth-simple"><div className="login-card mfa-card"><Logo/><QrCode size={34}/><span className="eyebrow">SEGURIDAD OWNER</span><h1>Verificación en dos pasos</h1>{challengeId ? <>{qrCode ? <><p>Escanea este código con tu aplicación de autenticación y después ingresa el código de seis dígitos.</p><img className="mfa-qr" src={qrCode} alt="Código QR para configurar TOTP"/></> : <p>Ingresa el código de seis dígitos de tu aplicación de autenticación.</p>}<form onSubmit={submit}><label>Código de 6 dígitos<input name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required autoFocus/></label>{message && <div className="form-message">{message}</div>}<button className="button primary wide" disabled={busy}>{busy ? "Verificando…" : "Verificar y entrar"}</button><button type="button" className="button secondary wide" onClick={() => void signOut()}>Cerrar sesión</button></form></> : <><p>No pudimos iniciar la verificación.</p>{message && <div className="form-message">{message}</div>}<button type="button" className="button primary wide" onClick={() => setSetupAttempt((value) => value + 1)}>Reintentar</button><button type="button" className="button secondary wide" onClick={() => void signOut()}>Cerrar sesión</button></>}</div></main>;
}

export function AuthCallbackPage() {
  const { completeAuthCallback } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const mounted = useRef(true);
  const tokenHash = params.get("token_hash"); const type = params.get("type") ?? "invite"; const returnTo = params.get("returnTo");
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!tokenHash) { setError("El enlace no contiene un token válido."); return; }
    window.history.replaceState(window.history.state, "", window.location.pathname);
    void completeAuthCallback(tokenHash, type, returnTo).then((result) => {
      if (!mounted.current) return;
      if (result.error) setError(result.error);
      else if (result.destination?.startsWith("/")) navigate(result.destination, { replace: true });
      else if (result.destination) window.location.replace(result.destination);
    });
  }, [completeAuthCallback, navigate, returnTo, tokenHash, type]);
  if (error) return <AccessErrorPage code="401" title="Enlace no válido" message={error}/>;
  return <div className="app-loader"><span/><strong>Validando acceso</strong></div>;
}

function PasswordFormPage({ mode }: { mode: "invite" | "recovery" }) {
  const { user, loading, accessContext, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (loading) return <div className="app-loader"><span/><strong>POSITRONICO</strong></div>;
  if (!user) return <Navigate to="/login" replace/>;
  if (mode === "invite" && !needsPasswordSetup(accessContext)) return <ExternalRedirect to={postLoginDestination(accessContext)}/>;
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setMessage(null);
    const data = new FormData(event.currentTarget); const password = String(data.get("password") ?? ""); const confirmation = String(data.get("confirmation") ?? "");
    if (password !== confirmation) { setMessage("Las contraseñas no coinciden."); setBusy(false); return; }
    const result = await updatePassword(password); setBusy(false);
    if (result.error) setMessage(result.error);
    else if (result.destination?.startsWith("/")) navigate(result.destination, { replace: true });
    else if (result.destination) window.location.replace(result.destination);
  };
  const invite = mode === "invite";
  return <main className="auth-simple"><div className="login-card"><Logo/><KeyRound size={34}/><span className="eyebrow">{invite ? "ACTIVA TU CUENTA" : "NUEVA CONTRASEÑA"}</span><h1>{invite ? "Crear tu contraseña" : "Restablece tu contraseña"}</h1><p>Usa al menos 12 caracteres, con mayúscula, minúscula y número. Puedes incluir símbolos.</p><form onSubmit={submit}><label>Nueva contraseña<input name="password" type="password" minLength={12} pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{12,}" required autoComplete="new-password"/></label><label>Confirmar contraseña<input name="confirmation" type="password" minLength={12} pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{12,}" required autoComplete="new-password"/></label>{message && <div className="form-message">{message}</div>}<button className="button primary wide" disabled={busy}>{busy ? "Guardando…" : invite ? "Crear contraseña y continuar" : "Guardar nueva contraseña"}</button></form></div></main>;
}

export function SetPasswordPage() { return <PasswordFormPage mode="invite"/>; }
export function ResetPasswordPage() { return <PasswordFormPage mode="recovery"/>; }

export function AppNotFoundPage() {
  const location = useLocation();
  return <AccessErrorPage code="404" title="Página no encontrada" message={`No existe una ruta para ${location.pathname}.`}/>;
}
